import { Link } from 'react-router-dom'
import { Card } from '@/components/ui/Card'
import type { VocabSummary } from '@/api/dashboard'
import { useMinCopy } from '@/i18n/useUiLocale'
import { copy } from '@/lib/copy'

interface VocabStatsWidgetProps {
  stats: VocabSummary
  isNewUser: boolean
  embedded?: boolean
}

function StatRow({
  label,
  count,
  color,
  muted,
}: {
  label: string
  count: number
  color: string
  muted?: boolean
}) {
  return (
    <div className="flex items-center justify-between text-sm">
      <div className="flex items-center gap-2">
        <span className={`h-2.5 w-2.5 rounded-full ${color}`} />
        <span className="text-muted-foreground">{label}</span>
      </div>
      <span className={`font-semibold tabular-nums ${muted ? 'text-muted-foreground/60' : ''}`}>
        {count}
      </span>
    </div>
  )
}

export function VocabStatsWidget({ stats, isNewUser, embedded = false }: VocabStatsWidgetProps) {
  const { dashboard, actions } = useMinCopy()

  const content = (
    <>
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {dashboard.vocabHeading}
        </p>
        <span
          className={`text-2xl font-bold tabular-nums ${isNewUser ? 'text-muted-foreground/50' : ''}`}
        >
          {isNewUser ? 0 : stats.total}
        </span>
      </div>

      {isNewUser ? (
        <p className="mt-2 text-xs text-muted-foreground">{copy.onboarding.vocabPreview}</p>
      ) : null}

      <div className="mt-4 space-y-2.5">
        <StatRow
          label={dashboard.statusNew}
          count={isNewUser ? 0 : Number(stats.newWords)}
          color="bg-slate-400"
          muted={isNewUser}
        />
        <StatRow
          label={dashboard.statusLearning}
          count={isNewUser ? 0 : Number(stats.learning)}
          color="bg-blue-500"
          muted={isNewUser}
        />
        <StatRow
          label={dashboard.statusReview}
          count={isNewUser ? 0 : Number(stats.review)}
          color="bg-amber-500"
          muted={isNewUser}
        />
        <StatRow
          label={dashboard.statusMastered}
          count={isNewUser ? 0 : Number(stats.mastered)}
          color="bg-green-500"
          muted={isNewUser}
        />
      </div>

      <div className="mt-4 flex items-center justify-between border-t pt-3 text-sm">
        <span className="text-muted-foreground">{dashboard.dueForReview}</span>
        <span
          className={`font-semibold tabular-nums ${
            !isNewUser && Number(stats.dueToday) > 0 ? 'text-blue-500' : 'text-muted-foreground/60'
          }`}
        >
          {isNewUser ? 0 : stats.dueToday}
        </span>
      </div>

      <Link
        to={isNewUser ? '/reading/recommended' : '/vocabulary'}
        className="mt-3 inline-flex h-8 w-full items-center justify-center rounded-md border border-input bg-background text-xs font-medium shadow-sm hover:bg-accent"
      >
        {isNewUser ? actions.startReading : dashboard.viewAllWords}
      </Link>
    </>
  )

  if (embedded) return content

  return <Card className="p-5">{content}</Card>
}
